import {
    createContext,
    lazy,
    Suspense,
    use,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type PropsWithChildren,
} from "react";

import { useFlags } from "launchdarkly-react-client-sdk";
import { track as trackEvent } from "@amplitude/analytics-browser";

import { FeatureFlag } from "@/constants";
import { useUser } from "@/contexts/user";
import {
    getSelectableModules,
    type OnboardingCondition,
    type UserRole,
} from "./tour-content";
import { getOnboardingState } from "./onboarding-tour-state";
import { useIsPartner } from "@/hooks/useIsPartner";
import { useIsStaff } from "@/hooks/useIsStaff";
import { useGetOrganisations } from "@/api/lookup-http-functions";
import { useCanAccessAllDevicesInOwnOrg } from "@/utils/user-permission";
import { Spinner } from "@/components/ui/spinner";

const OnboardingTour = lazy(() => import("./onboarding-tour"));

interface OnboardingTourContextValue {
    isAvailable: boolean;
    isTourActive: boolean;
    startTour: () => void;
}

const OnboardingTourContext = createContext<OnboardingTourContextValue>({
    isAvailable: false,
    isTourActive: false,
    startTour: () => {},
});

export const useOnboardingTour = () => use(OnboardingTourContext);

export const usePreventOutsideCloseDuringTour = () => {
    const { isTourActive } = useOnboardingTour();
    return (e: Event) => {
        if (isTourActive) e.preventDefault();
    };
};

export function useOnboardingAutoStart({ enabled }: { enabled: boolean }) {
    const { isAvailable, startTour } = useOnboardingTour();
    const { user } = useUser();
    const hasTriggered = useRef(false);

    useEffect(() => {
        if (!enabled) return;
        if (hasTriggered.current) return;
        if (!isAvailable || user?.userAccountId === undefined) return;
        if (getOnboardingState(user.userAccountId) !== null) return;

        hasTriggered.current = true;
        startTour();
    }, [enabled, isAvailable, startTour, user?.userAccountId]);
}

export const OnboardingTourProvider = ({ children }: PropsWithChildren) => {
    const flags = useFlags();
    const { user } = useUser();
    const userId = user?.userAccountId;

    const isStaff = useIsStaff();
    const isPartner = useIsPartner();
    const userRole: UserRole = isStaff
        ? "staff"
        : isPartner
          ? "partner"
          : "customer";

    const permissions = user?.permissions ?? [];

    const { data: { organisations } = {} } = useGetOrganisations();
    const { canAccessAllDevicesInOwnOrg } = useCanAccessAllDevicesInOwnOrg();

    const conditions = useMemo<OnboardingCondition[]>(() => {
        const orgCount = organisations?.length ?? 0;
        const result: OnboardingCondition[] = [];
        if (canAccessAllDevicesInOwnOrg) result.push("single_org");
        if (orgCount > 1) result.push("multi_org");
        return result;
    }, [organisations, canAccessAllDevicesInOwnOrg]);

    const selectableModules = useMemo(
        () => getSelectableModules(user?.permissions ?? [], userRole),
        [user?.permissions, userRole],
    );

    const isAvailable =
        !!flags[FeatureFlag.IL_4090_ONBOARDING_TUTORIAL_CUSTOMER] &&
        selectableModules.length > 0 &&
        userId != undefined;

    const [isActive, setIsActive] = useState(false);

    const startTour = useCallback(() => setIsActive(true), []);
    const endTour = useCallback(() => setIsActive(false), []);

    const value = useMemo<OnboardingTourContextValue>(
        () => ({ isAvailable, isTourActive: isActive, startTour }),
        [isAvailable, isActive, startTour],
    );

    return (
        <OnboardingTourContext value={value}>
            {children}
            {isActive && isAvailable && userId != undefined && (
                <Suspense fallback={<Spinner sizing="large" />}>
                    <OnboardingTour
                        selectableModules={selectableModules}
                        permissions={permissions}
                        conditions={conditions}
                        userRole={userRole}
                        userId={userId}
                        onClose={endTour}
                        onTrackEvent={(event, props) =>
                            trackEvent(event, props ?? {})
                        }
                    />
                </Suspense>
            )}
        </OnboardingTourContext>
    );
};
